import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { LMBuilderEvent, LMBuilderModel, } from "@components/builder/services/lm-builder-model.service";
import { AppConstants } from "@app/utils";
import { LMComputeBuilderService, LMFieldPickerService } from "@app/components";
import { LMContextService, LMErrorUtilService, LMUrlUtilService } from "@app/services";
import { SohoAlertDirective } from "ids-enterprise-ng";
import { LMFieldPickerOperatorOptions, LMFieldPickerSelectResponse } from "@modal/field-picker/lm-field-picker";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";


@Component({
  selector:    'lm-builder-display-field',
  standalone: false,
  templateUrl: './lm-builder-display-field.component.html',
  styleUrl: './lm-builder-display-field.component.scss',
})
export class LMBuilderDisplayFieldComponent implements OnInit, OnDestroy {

  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective> | undefined;
  
  @Input() spec!: LMListPSpec;
  @Input() displayField!: LMDisplayFieldPSpec | undefined;
  @Input() fieldSpec: LMFieldPSpec | undefined;

  label = '';
  groupLabel = '';
  canUpdate = false;
  allowUpdate = false;
  allowUpdateWhen = '';
  allowUpdateMessage = '';
  supportsOperator = false;
  enableOperatorForActor = false;
  editOperatorLabel = 'Change Operator';

  totalIDSAttributes = [ { name: 'data-automation-id', value: 'lm-builder-display-field-total' } ];
  totalTypes: Array<TotalOption> = [];
  canTotal = false;
  computeFieldValidator$:Subject<string> | undefined;

  formTextSpec: LMFormTextPSec | undefined;

  protected readonly LMBuilderModel = LMBuilderModel;
  private readonly destroyer$ = new Subject<void>();

  constructor(
    private lmBuilderModel: LMBuilderModel,
    private lmFieldPickerService: LMFieldPickerService,
    private lmContextService: LMContextService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmComputeBuilderService: LMComputeBuilderService,
  ) {
      //no-op
  }

  ngOnInit(): void {
    if (this.displayField && this.displayField.type === 'formText') {

      this.formTextSpec = this.displayField.spec as LMFormTextPSec;
      return;
    }

    this.initializeLabel();
    this.initializeGroupLabel();
    this.initializeAllowUpdate();
    this.initializeOperator();
    this.initializeTotalType();
    this.initializeInlineCompute();

    if (this.fieldSpec && this.fieldSpec.inlineCompute) {
      this.validateCompute();
    }
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private initializeInlineCompute() {
    if (!this.computeFieldValidator$) {
      this.computeFieldValidator$ = new Subject<string>();
      this.computeFieldValidator$.pipe(
          takeUntil(this.destroyer$),
          debounceTime(1250),
          distinctUntilChanged())
          .subscribe(value => {
            this.validateCompute();
          });
    }

    // @ts-ignore
    Soho.Validation.rules['validateRequired'] = this.lmBuilderModel.validateRequired();
  }

  private initializeTotalType() {
    this.canTotal = this.fieldSpec ? this.fieldSpec.canTotal : false;
    this.totalTypes = [];
    if (this.canTotal) {
      if (this.spec.implementsColumnarView) {
        this.totalTypes.push({ value: 'NONE', label: Soho.Locale.translate('totaling.option.label.none') });
        this.totalTypes.push({ value: 'TOTAL', label: Soho.Locale.translate('totaling.option.label.total') });
      } else {
        this.totalTypes.push({ value: 'NONE', label: Soho.Locale.translate('totaling.option.label.none') });
        this.totalTypes.push({ value: 'TOTAL', label: Soho.Locale.translate('totaling.option.label.total') });
        this.totalTypes.push({ value: 'RUNNING_TOTAL', label: Soho.Locale.translate('totaling.option.label.running') });
        this.totalTypes.push({ value: 'PERCENT_OF_TOTAL', label: Soho.Locale.translate('totaling.option.label.percent') });
      }
    } else {
      this.totalTypes.push({ value: 'NONE', label: Soho.Locale.translate('totaling.option.label.none') });
    }
  }

  private initializeOperator() {
    if (this.spec && this.fieldSpec && this.fieldSpec.uiFieldSpec) {
      this.supportsOperator = this.lmBuilderModel.isAuditDataSource() || this.lmFieldPickerService.supportsFieldOperator(this.spec.implementsCubeView ? 'Measures' :'Fields', this.fieldSpec.uiFieldSpec);
      this.enableOperatorForActor = this.spec.implementsCubeView || this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_Configuration);
      this.editOperatorLabel = this.fieldSpec.operatorInfo ? 'Change Operator' : 'Change Operator';
    }
  }

  private initializeAllowUpdate() {
    if (this.fieldSpec) {
      this.allowUpdate = this.fieldSpec.allowUpdate.allowUpdate;
      this.allowUpdateWhen = this.fieldSpec.allowUpdate.when;
      this.updateCanUpdate();
      this.updateAllowUpdateAlert();
    }
  }

  private updateCanUpdate() {
    if (this.fieldSpec) {
      this.canUpdate = this.spec.businessClass === this.fieldSpec.uiFieldSpec?.businessClass && this.fieldSpec.canUpdate && this.fieldSpec.operatorInfo === undefined;
    }
  }

  private updateAllowUpdateAlert() {
    if (this.fieldSpec) {
      this.updateCanUpdate();
      if (!this.fieldSpec.canUpdate) {
        this.allowUpdateMessage = 'Field not editable';
      } else if (this.fieldSpec.operatorInfo !== undefined) {
        this.allowUpdateMessage = 'Field not editable when it has an operator';
      } else {
        this.allowUpdateMessage = '';
      }
    }
  }

  private initializeLabel() {
    if (this.displayField) {
      this.label = this.displayField.label.fields ? this.displayField.label.messageRule : this.displayField.label.display;
    }
  }

  private initializeGroupLabel() {
    if (this.displayField && this.displayField.groupLabel) {
      this.groupLabel = this.displayField.groupLabel.fields ? this.displayField.groupLabel.messageRule : this.displayField.groupLabel.display;
    }
  }

  editOperator() {
    if (this.spec && this.fieldSpec && this.fieldSpec.uiFieldSpec) {
      const options: LMFieldPickerOperatorOptions = {
        view: this.spec.implementsCubeView ? 'Measures' : 'Fields',
        businessClass: this.spec.businessClass,
        uiFieldSpec: this.fieldSpec.uiFieldSpec,
        operatorInfo: this.fieldSpec.operatorInfo,
        auditData: this.lmBuilderModel.isAuditDataSource()
      };

      return this.lmFieldPickerService.openFieldOperatorDialog(options).subscribe({
        next: (v)         => { this.updateFieldId(v);           },
        error: (e)        => { this.handleError(e);             },
        complete: ()      => {                                  }
      });
    }
  }

  private updateFieldId(response: LMFieldPickerSelectResponse) {
    if (this.fieldSpec && response) {
      this.lmBuilderModel.updateFieldOperator(this.fieldSpec, response.fieldName, response.uiFieldSpec, response.operatorInfo);
      this.editOperatorLabel = this.fieldSpec.operatorInfo ? 'Change Operator' : 'Change Operator';
      this.updateAllowUpdateAlert();
    }
  }

  private handleError(error: any) {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  updateTotal() {
    this.lmBuilderModel.builderEvent$.next(LMBuilderEvent.DISPLAY_FIELD_TOTAL_CHANGED);
  }

  updateAllowUpdate() {
    if (this.fieldSpec) {
      this.lmBuilderModel.updateAllowUpdate(this.fieldSpec, this.allowUpdate, this.allowUpdateWhen);
    }
  }

  updateLabel() {
    if (this.displayField) {
      this.lmBuilderModel.updateDisplayFieldLabel(this.displayField, this.label);
    }
  }

  updateGroupLabel() {
    if (this.displayField && this.displayField.groupLabel) {
      this.displayField.groupLabel.messageRule = this.groupLabel;
      this.displayField.groupLabel.display = this.label;
    }
  }

  openComputeBuilder() {

    if (!this.fieldSpec) {
      return;
    }

    let fieldPickerUrl = this.lmBuilderModel.getBuilderUrl();
    fieldPickerUrl = this.lmUrlUtilService.addParamToUrl(fieldPickerUrl, 'requesttype', 'fieldSpecForBusClass');
    const validationUrl = this.lmUrlUtilService.getConfigUrl(this.lmBuilderModel.listResource.resourceId);
    const includeAudit = this.lmBuilderModel.isAuditDataSource();
    const hasComputeText = this.fieldSpec.fieldId.trim().length > 0;
    this.lmComputeBuilderService.openComputeBuilderDialog(this.lmBuilderModel.listResource.busClassName,
        fieldPickerUrl, fieldPickerUrl, validationUrl, hasComputeText, false, includeAudit,
        undefined, undefined, "basic").pipe(
        takeUntil(this.destroyer$),).subscribe((result: LMComputeBuilderResult) => {

      if (this.fieldSpec && result.lplQuery && result.lplQuery !== this.fieldSpec.fieldId ) {
        if (result.action === 'Append') {
          this.fieldSpec.fieldId = `${this.fieldSpec.fieldId} ${result.lplQuery}`;
        }
        if (result.action === 'Ok' || result.action === 'Overwrite') {
          this.fieldSpec.fieldId = result.lplQuery;
        }
        this.validateCompute();
      }
    });
  }

  validateCompute() {
    if (this.fieldSpec) {
       this.lmBuilderModel.validateCompute(this.fieldSpec.fieldId).pipe(
          takeUntil(this.destroyer$),
          debounceTime(1250),
          distinctUntilChanged()).subscribe({
        next: (v)       => { this.updateComputeValidationAlert(v); },
        error: (e)                      => { this.handleError(e); },
        complete: ()                          => { }
      });
    }
  }

  validateComputeInParens() {
    if (this.fieldSpec) {
      this.fieldSpec.fieldId = this.ensureParens(this.fieldSpec.fieldId );
    }
  }

  private ensureParens(someString: string): string {
    if (someString.trim().length === 0) {
      return '';
    }
    if (someString.indexOf('(') !== 0) {
      someString = '(' + someString;
      if ((someString.match(/\(/g)||[]).length !==  (someString.match(/\)/g)||[]).length) {
        someString += ')';
      }
    }

    if (someString.lastIndexOf(')') !== someString.length - 1) {
      someString += ')';
      if ((someString.match(/\(/g)||[]).length !==  (someString.match(/\)/g)||[]).length) {
        someString = '(' + someString;
      }
    }

    return someString;
  }

  private updateComputeValidationAlert(lplValidationResponse?: LMLplValidationResponse) {
    const alert = this.alerts?.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === 'lm-display-field-compute');
    if (alert) {
      if (!lplValidationResponse || lplValidationResponse.lplStatus === 'LPL_VALID') {
        alert.removeAllMessages();
      } else {
        const validationError = lplValidationResponse.validationError;
        if (validationError) {
          let alertMessage = validationError.errorDescription;
          if (validationError.errorText) {
            alertMessage += ' ' + validationError.errorText;
          }
          const alertType = validationError && validationError.isWarning ? 'info' : 'error';
          alert.addInlineMessage(alertMessage, alertType, true, false, undefined, 'lm-builder-list-filter');
        }
      }
    }
  }
}
